/**
 * PasswordInput — Input with a show/hide eye toggle.
 *
 * Behaviour:
 *   * type defaults to "password" (masked).
 *   * Clicking the eye button toggles to "text" (visible) and back.
 *   * Toggle button is inside the field on the right, absolute-positioned,
 *     so it works on mobile / tablet / desktop identically.
 *   * All standard <Input /> props (value, onChange, autoComplete, required,
 *     placeholder, data-testid, className, ...) are forwarded transparently.
 */
import { useState, forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { Eye, EyeSlash } from "@phosphor-icons/react";

const PasswordInput = forwardRef(function PasswordInput(
  { className = "", inputClassName = "", "data-testid": testId, toggleTestId, ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeSlash : Eye;
  const toggle = () => setVisible(v => !v);
  const derivedToggleTestId =
    toggleTestId || (testId ? `${testId}-toggle` : "password-visibility-toggle");

  return (
    <div className={`relative ${className}`}>
      <Input
        ref={ref}
        type={visible ? "text" : "password"}
        className={`pr-10 ${inputClassName}`}
        data-testid={testId}
        {...rest}
      />
      <button
        type="button"
        tabIndex={-1}
        onMouseDown={(e) => e.preventDefault()}
        onClick={toggle}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        data-testid={derivedToggleTestId}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-r-md"
      >
        <Icon size={16} weight="regular" />
      </button>
    </div>
  );
});

export default PasswordInput;
export { PasswordInput };
